'use client';

import { c as _c } from "react/compiler-runtime";
import type { AuthSuccessToastMessages } from '@morfi/backend-sdk-v2/client';
import { SdkAuthSuccessHandler, SdkNetworkAuthBridge, useSdkTurnkey } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect } from 'react';
import { OnboardingCompletionSync } from '@/app/[locale]/onboarding/_components/onboarding-completion-sync';
import { useOnboardingStore } from '@/app/[locale]/onboarding/_hooks/use-onboarding-store';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { identifyUser } from '@/lib/analytics';
import { captureReturnToAfterAuth, consumeNextAfterAuth } from '@/lib/auth/next-url';
import { isMainnetEnabled, resolveAuthRedirect } from '@/lib/network';
import { useNetworkStore } from '@/lib/network/store';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { useAppRouter } from '@/lib/network/use-app-router';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { useLayoutStore } from '@/stores/layout';

/**
 * Bridge between the layout store's `connectAuthOpen` flag and Turnkey's
 * `handleLogin()` modal. Many components call `setConnectAuthOpen(true)` to
 * trigger the auth flow — this component watches that flag and opens
 * Turnkey's managed login modal when it becomes true.
 */
function AuthModalBridge() {
  const $ = _c(5);
  const connectAuthOpen = useLayoutStore(_temp);
  const setConnectAuthOpen = useLayoutStore(_temp2);
  const {
    handleLogin
  } = useSdkTurnkey();
  let t0;
  let t1;
  if ($[0] !== connectAuthOpen || $[1] !== handleLogin || $[2] !== setConnectAuthOpen) {
    t0 = () => {
      if (connectAuthOpen) {
        setConnectAuthOpen(false);
        captureReturnToAfterAuth();
        handleLogin();
      }
    };
    t1 = [connectAuthOpen, setConnectAuthOpen, handleLogin];
    $[0] = connectAuthOpen;
    $[1] = handleLogin;
    $[2] = setConnectAuthOpen;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return null;
}

/**
 * Wires app-specific callbacks into `<SdkAuthSuccessHandler>` and mounts
 * the SDK network bridge. Separated into its own component so hooks that
 * need router/translations context run inside the intl provider.
 */
function _temp2(s_0) {
  return s_0.setConnectAuthOpen;
}
function _temp(s) {
  return s.connectAuthOpen;
}
function AuthHandlers() {
  const $ = _c(38);
  const router = useAppRouter();
  const t = useTranslations("auth");
  const userNetwork = useNetworkStore(_temp3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = isMainnetEnabled();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const mainnetEnabled = t0;
  const network = useActiveNetwork();
  let t1;
  if ($[1] !== t) {
    t1 = t("toast.signing_in");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("toast.account_created");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("toast.welcome");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("toast.error");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  let t6;
  if ($[9] !== t) {
    t5 = provider => t("toast.signed_up_with_provider", {
      provider
    });
    t6 = method => t("toast.wallet_already_linked", {
      method
    });
    $[9] = t;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = {
      signingIn: t1,
      accountCreated: t2,
      welcome: t3,
      error: t4,
      signedUpWithProvider: t5,
      walletAlreadyLinked: t6
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const toastMessages = t7;
  let t8;
  if ($[19] !== t) {
    t8 = () => {
      showToast({
        id: TOAST_IDS.AUTH_SYNCING,
        message: t("toast.signing_in"),
        variant: "loading"
      });
    };
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t10;
  let t9;
  if ($[21] !== t) {
    t9 = displayName => {
      showToast({
        id: TOAST_IDS.AUTH_ACCOUNT_CREATED,
        message: displayName !== null && displayName !== "" ? t("toast.account_created_named", {
          name: displayName
        }) : t("toast.account_created"),
        variant: "success"
      });
    };
    t10 = displayName_0 => {
      showToast({
        id: TOAST_IDS.AUTH_WELCOME,
        message: displayName_0 !== null && displayName_0 !== "" ? t("toast.welcome_named", {
          name: displayName_0
        }) : t("toast.welcome"),
        variant: "success"
      });
    };
    $[21] = t;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  let t11;
  if ($[24] !== router || $[25] !== userNetwork) {
    t11 = path => {
      router.push(resolveAuthRedirect(path, userNetwork, mainnetEnabled));
    };
    $[24] = router;
    $[25] = userNetwork;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t8 || $[30] !== t9 || $[31] !== toastMessages) {
    t12 = <SdkAuthSuccessHandler callbacks={{
      onShowSigningInToast: t8,
      onDismissSigningInToast: _temp4,
      onShowAccountCreatedToast: t9,
      onShowWelcomeToast: t10,
      onShowErrorToast: _temp5,
      onNavigate: t11,
      onConsumeNextAfterAuth: _temp6,
      onIdentifyUser: _temp7,
      onEvent: _temp8,
      onResetOnboardingStore: _temp9,
      toastMessages
    }} />;
    $[27] = t10;
    $[28] = t11;
    $[29] = t8;
    $[30] = t9;
    $[31] = toastMessages;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== network) {
    t13 = <SdkNetworkAuthBridge network={network} />;
    $[33] = network;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}

/** Auth completion runs for app routes and OAuth callback routes. */
function _temp9() {
  useOnboardingStore.getState().reset();
}
function _temp8(name, payload) {
  onEvent(name as Parameters<typeof onEvent>[0], payload as never);
}
function _temp7(userId) {
  identifyUser(userId, {});
}
function _temp6() {
  return consumeNextAfterAuth();
}
function _temp5(message) {
  showToast({
    id: TOAST_IDS.AUTH_ERROR,
    message,
    variant: "error",
    duration: 8000
  });
}
function _temp4() {
  dismissToast(TOAST_IDS.AUTH_SYNCING);
}
function _temp3(s) {
  return s.current;
}
export function GlobalAuthHandlers() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><AuthHandlers /><AuthModalBridge /><OnboardingCompletionSync /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}